import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import '@progress/kendo-theme-default/dist/all.css';
import axios from 'axios';
import { getConfig } from '../../Config';
import {
  Participant,
  Session,
  TaskMetadata,
  SessionDataItem,
  RawParticipantData,
  RegistrationState,
} from '../../models/dashboardTypes';
import { Gender, Diagnosis, ApoEStatus, AmyloidStatus } from '../../models/participantRegistration';
import { formatDate } from '../../utils/dateUtils';
import { getGenderDisplayName } from '../../utils/genderUtils';
import { Colors } from '../../styles/colors';
import { MAX_SESSIONS } from '../../constants/sessions';
import { createUploadStatusRow, SessionTableRow, MAX_FILES } from '../../utils/sessionUtils';
import OffboardFormDialog from '../../components/dialogs/OffboardFormDialog';
import OffboardConfirmDialog from '../../components/dialogs/OffboardConfirmDialog';
import OffboardSuccessDialog from '../../components/dialogs/OffboardSuccessDialog';
import EnrollDeviceDialog from '../../components/dialogs/EnrollDeviceDialog';
import GeneratingCodeDialog from '../../components/dialogs/GeneratingCodeDialog';
import EnrollmentSuccessDialog from '../../components/dialogs/EnrollmentSuccessDialog';
import ErrorDialog from '../../components/dialogs/ErrorDialog';
import '../../components/dialogs/Dialogs.scss';
import './Dashboard.scss';

const getRegistrationState = (participant: Participant): RegistrationState => {
  if (participant.registrationState) {
    return participant.registrationState;
  }
  // For older entries without registrationState
  return participant.activatedOn ? 'ACTIVE' : 'ONBOARD';
};

const isRejoinWindowOpen = (participant: Participant): boolean => {
  if (getRegistrationState(participant) !== 'OFFBOARD') {
    return false;
  }

  if (!participant.offboardedOn) {
    return false;
  }

  const offboardDate = new Date((participant.offboardedOn as number) * 1000);
  const now = new Date();
  return offboardDate > now;
};

interface DetailPageProps {
  isAuthReady: boolean;
}

const FIELD_NAMES = {
  BACKGROUND_VOICE: 'Background voice',
  LOW_VOICE: 'Low voice',
  NOISE: 'Noise',
  PII: 'PII',
  SILENCE: 'Silence',
  PHQ_8: 'PHQ-8',
  PVT: 'PVT',
  SLEEPINESS_SCALE: 'Sleepiness scale',
  OPEN_ENDED_QUESTION_1: 'Open ended question 1',
  OPEN_ENDED_QUESTION_2: 'Open ended question 2',
  OPEN_ENDED_QUESTION_3: 'Open ended question 3',
  PICTURE_DESCRIPTION_1: 'Picture description 1',
  PICTURE_DESCRIPTION_2: 'Picture description 2',
  PICTURE_RECALL: 'Picture recall',
  STORY_RECALL_1: 'Story recall 1',
  STORY_RECALL_2: 'Story recall 2',
  STORY_TELLING: 'Story telling',
} as const;

const BOOLEAN_FIELDS = new Set<string>([
  FIELD_NAMES.BACKGROUND_VOICE,
  FIELD_NAMES.LOW_VOICE,
  FIELD_NAMES.NOISE,
  FIELD_NAMES.PII,
  FIELD_NAMES.SILENCE,
]);

const SESSION_FIELDS = [
  FIELD_NAMES.BACKGROUND_VOICE,
  FIELD_NAMES.LOW_VOICE,
  FIELD_NAMES.NOISE,
  FIELD_NAMES.PII,
  FIELD_NAMES.SILENCE,
  FIELD_NAMES.PHQ_8,
  FIELD_NAMES.PVT,
  FIELD_NAMES.SLEEPINESS_SCALE,
  FIELD_NAMES.OPEN_ENDED_QUESTION_1,
  FIELD_NAMES.OPEN_ENDED_QUESTION_2,
  FIELD_NAMES.OPEN_ENDED_QUESTION_3,
  FIELD_NAMES.PICTURE_DESCRIPTION_1,
  FIELD_NAMES.PICTURE_DESCRIPTION_2,
  FIELD_NAMES.PICTURE_RECALL,
  FIELD_NAMES.STORY_RECALL_1,
  FIELD_NAMES.STORY_RECALL_2,
  FIELD_NAMES.STORY_TELLING,
] as const;

const FIELD_EXTRACTORS: Record<string, (session: SessionDataItem) => string> = {
  [FIELD_NAMES.BACKGROUND_VOICE]: (session) => (session.circadicMetadata?.backgroundVoicesDetected ? 'true' : 'false'),
  [FIELD_NAMES.LOW_VOICE]: (session) => (session.circadicMetadata?.lowVoice ? 'true' : 'false'),
  [FIELD_NAMES.NOISE]: (session) => (session.circadicMetadata?.noiseDetected ? 'true' : 'false'),
  [FIELD_NAMES.PII]: (session) => (session.circadicMetadata?.containsPII ? 'true' : 'false'),
  [FIELD_NAMES.SILENCE]: (session) => (session.circadicMetadata?.silenceDetected ? 'true' : 'false'),
  [FIELD_NAMES.PHQ_8]: (session) => {
    const phqTask = session.appMetadata?.tasksMetadata?.find((t: TaskMetadata) => t.taskType === 'PHQ-8');
    return phqTask ? `${phqTask.total || 0}` : '';
  },
  [FIELD_NAMES.PVT]: (session) => {
    const pvtTask = session.appMetadata?.tasksMetadata?.find((t: TaskMetadata) => t.taskType === 'PVT');
    return pvtTask ? `${pvtTask.meanCorrectHitsReactionTime || 0}` : '';
  },
  [FIELD_NAMES.SLEEPINESS_SCALE]: (session) => {
    const sleepTask = session.appMetadata?.tasksMetadata?.find((t: TaskMetadata) => t.taskType === 'SleepinessScale');
    return sleepTask ? `${sleepTask.sleepinessAnswer || 0}` : '';
  },
  [FIELD_NAMES.OPEN_ENDED_QUESTION_1]: (session) => {
    const que1Task = session.appMetadata?.tasksMetadata?.find((t: TaskMetadata) => t.filename === 'task03.flac');
    return que1Task ? `${que1Task.duration || 0}` : '';
  },
  [FIELD_NAMES.OPEN_ENDED_QUESTION_2]: (session) => {
    const que2Task = session.appMetadata?.tasksMetadata?.find((t: TaskMetadata) => t.filename === 'task04.flac');
    return que2Task ? `${que2Task.duration || 0}` : '';
  },
  [FIELD_NAMES.OPEN_ENDED_QUESTION_3]: (session) => {
    const que3Task = session.appMetadata?.tasksMetadata?.find((t: TaskMetadata) => t.filename === 'task05.flac');
    return que3Task ? `${que3Task.duration || 0}` : '';
  },
  [FIELD_NAMES.PICTURE_DESCRIPTION_1]: (session) => {
    const pdpic1Task = session.appMetadata?.tasksMetadata?.find((t: TaskMetadata) => t.filename === 'task11.flac');
    return pdpic1Task ? `${pdpic1Task.duration || 0}` : '';
  },
  [FIELD_NAMES.PICTURE_DESCRIPTION_2]: (session) => {
    const pdpic2Task = session.appMetadata?.tasksMetadata?.find((t: TaskMetadata) => t.filename === 'task01.flac');
    return pdpic2Task ? `${pdpic2Task.duration || 0}` : '';
  },
  [FIELD_NAMES.PICTURE_RECALL]: (session) => {
    const recallTask = session.appMetadata?.tasksMetadata?.find((t: TaskMetadata) => t.filename === 'task06.flac');
    return recallTask ? `${recallTask.duration || 0}` : '';
  },
  [FIELD_NAMES.STORY_RECALL_1]: (session) => {
    const storyRecall1Task = session.appMetadata?.tasksMetadata?.find(
      (t: TaskMetadata) => t.filename === 'task07.flac'
    );
    return storyRecall1Task ? `${storyRecall1Task.duration || 0}` : '';
  },
  [FIELD_NAMES.STORY_RECALL_2]: (session) => {
    const storyRecall2Task = session.appMetadata?.tasksMetadata?.find(
      (t: TaskMetadata) => t.filename === 'task09.flac'
    );
    return storyRecall2Task ? `${storyRecall2Task.duration || 0}` : '';
  },
  [FIELD_NAMES.STORY_TELLING]: (session) => {
    const storyTellTask = session.appMetadata?.tasksMetadata?.find((t: TaskMetadata) => t.filename === 'task10.flac');
    return storyTellTask ? `${storyTellTask.duration || 0}` : '';
  },
};

const createDateRow = (sessions: SessionDataItem[]): SessionTableRow => {
  const dateRow: SessionTableRow = { field: 'Date' };

  sessions.forEach((session) => {
    const sessionKey = session.sessionNumber;
    if (!sessionKey) return;

    const completionDate = session.appMetadata.sessionMetadata?.sessionEndDateTime
      ? formatDate(new Date(session.appMetadata.sessionMetadata.sessionEndDateTime))
      : '';

    dateRow[sessionKey] = completionDate;
  });

  return dateRow;
};

const createFieldRow = (fieldName: string, sessions: SessionDataItem[]): SessionTableRow => {
  const row: SessionTableRow = { field: fieldName };

  sessions.forEach((session) => {
    const sessionKey = session.sessionNumber;
    if (!sessionKey) return;

    const extractor = FIELD_EXTRACTORS[fieldName];
    const value = extractor ? extractor(session) : '';

    row[sessionKey] = value;
  });

  return row;
};

const BooleanCell = (props: { dataItem: SessionTableRow; field?: string }) => {
  const value = props.dataItem[props.field || ''];
  const boolValue = value === 'true' ? true : value === 'false' ? false : null;

  let backgroundColor = 'inherit';
  if (boolValue === false) {
    backgroundColor = Colors.green;
  } else if (boolValue === true) {
    backgroundColor = Colors.red;
  }

  return <td style={{ backgroundColor }}>{value}</td>;
};

const UploadStatusCell = (props: { dataItem: SessionTableRow; field?: string }) => {
  const value = props.dataItem[props.field || ''];
  const maxFilesValue = `${MAX_FILES}/${MAX_FILES}`;
  const unknownValue = `?/${MAX_FILES}`;

  let backgroundColor = 'inherit';
  if (value === maxFilesValue) {
    backgroundColor = Colors.green;
  } else if (value === unknownValue) {
    backgroundColor = Colors.yellow;
  } else if (value && value !== maxFilesValue) {
    backgroundColor = Colors.red;
  }

  return <td style={{ backgroundColor }}>{value}</td>;
};

// Conditional cell that applies color coding only to boolean fields and upload status
const ConditionalCell = (props: { dataItem: SessionTableRow; field?: string }) => {
  const fieldName = props.dataItem.field;

  if (BOOLEAN_FIELDS.has(fieldName)) {
    return BooleanCell(props);
  }

  if (fieldName === 'Upload Status') {
    return UploadStatusCell(props);
  }

  // Default cell for non-boolean fields
  const value = props.dataItem[props.field || ''];
  return <td>{value}</td>;
};

const DetailPage: React.FC<DetailPageProps> = ({ isAuthReady }) => {
  const { participantUID } = useParams<{ participantUID: string }>();
  const navigate = useNavigate();

  const [participant, setParticipant] = useState<Participant | null>(null);
  const [appMetadata, setAppMetadata] = useState<SessionDataItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showOffboardDialog, setShowOffboardDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showEnrollDeviceDialog, setShowEnrollDeviceDialog] = useState(false);
  const [showGeneratingCodeDialog, setShowGeneratingCodeDialog] = useState(false);
  const [showEnrollmentSuccessDialog, setShowEnrollmentSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [wasRejoinAction, setWasRejoinAction] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const [offboardParticipantId, setOffboardParticipantId] = useState('');
  const [offboardParticipantGender, setOffboardParticipantGender] = useState('');

  const processParticipantData = useCallback((rawParticipant: RawParticipantData): Participant => {
    return {
      '@odata.id': '',
      '@odata.etag': '',
      '@odata.editLink': '',
      id: rawParticipant.id,
      siteID: rawParticipant.siteID,
      participantUID: rawParticipant.participantUID,
      createdOn: rawParticipant.createdOn ? new Date(rawParticipant.createdOn * 1000) : new Date(),
      activatedOn: rawParticipant.activatedOn ? new Date(rawParticipant.activatedOn * 1000) : null,
      offboardedOn: rawParticipant.offboardedOn,
      age: rawParticipant.age,
      gender: rawParticipant.gender as Gender,
      diagnosis: rawParticipant.diagnosis as Diagnosis,
      apoEStatus: rawParticipant.apoEStatus as ApoEStatus,
      amyloidStatus: rawParticipant.amyloidStatus as AmyloidStatus,
      educationYears: rawParticipant.educationYears,
      contentPackId: rawParticipant.contentPackId,
      sessions: (rawParticipant.sessions || []) as Session[],
      registrationState:
        (rawParticipant.registrationState as RegistrationState) || (rawParticipant.activatedOn ? 'ACTIVE' : 'ONBOARD'),
    };
  }, []);

  const handleApiError = (err: unknown, context: string): string => {
    if (err && typeof err === 'object' && 'response' in err) {
      const axiosError = err as { response?: { status?: number }; message?: string };
      if (axiosError.response?.status === 404) {
        return `${context}: Participant not found`;
      }
      if (axiosError.response?.status === 403) {
        return `${context}: Access denied`;
      }
      return `${context}: ${axiosError.message || 'Unknown error'}`;
    }
    return `${context}: Unknown error`;
  };

  useEffect(() => {
    if (!isAuthReady || !participantUID) return;

    const fetchParticipantData = async () => {
      try {
        setLoading(true);
        setError(null);

        const config = getConfig();
        const baseUrl = config.apiEndpoint;

        const participantUrl = `${baseUrl}/participants(${participantUID})`;
        const participantResponse = await axios.get(participantUrl);

        const processedParticipant = processParticipantData(participantResponse.data.value);
        setParticipant(processedParticipant);

        try {
          const metadataUrl = `${baseUrl}/participants(${participantUID})/metadata`;
          const metadataResponse = await axios.get(metadataUrl);

          const metadataArray = Array.isArray(metadataResponse.data.value) ? metadataResponse.data.value : [];

          setAppMetadata(metadataArray);
        } catch (metadataError) {
          setAppMetadata([]);
        }
      } catch (err) {
        setError(handleApiError(err, 'Failed to load participant details'));
      } finally {
        setLoading(false);
      }
    };

    fetchParticipantData();
  }, [isAuthReady, participantUID, processParticipantData]);

  const gridData = useMemo((): SessionTableRow[] => {
    if (appMetadata.length === 0) {
      return [];
    }

    const rows: SessionTableRow[] = [];

    rows.push(createUploadStatusRow(appMetadata));
    rows.push(createDateRow(appMetadata));

    SESSION_FIELDS.forEach((fieldName) => {
      rows.push(createFieldRow(fieldName, appMetadata));
    });

    return rows;
  }, [appMetadata]);

  const sessionColumns = useMemo(() => {
    const allSessions = Array.from({ length: MAX_SESSIONS }, (_, index) => {
      const sessionNumber = index + 1;
      const sessionKey = `session${sessionNumber.toString().padStart(2, '0')}`;

      return (
        <Column
          key={sessionKey}
          field={sessionKey}
          title={sessionNumber.toString()}
          cells={{ data: ConditionalCell }}
        />
      );
    });

    return allSessions;
  }, []);

  if (loading) {
    return (
      <div className="dashboard-container">
        <h2>Loading participant details...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!participant) {
    return (
      <div className="dashboard-container">
        <h2>Participant not found</h2>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-controls">
        <h2>
          {participant.id} | {participant.siteID} | {participant.contentPackId}
        </h2>
        <div className="detail-button-group">
          <a className="dashboard-button" role="button" onClick={() => setShowEnrollDeviceDialog(true)}>
            Enroll new device
          </a>
          <a
            className={`dashboard-button ${
              (getRegistrationState(participant) === 'OFFBOARD' && !isRejoinWindowOpen(participant)) ||
              (getRegistrationState(participant) !== 'ACTIVE' && getRegistrationState(participant) !== 'OFFBOARD')
                ? 'disabled'
                : ''
            }`}
            role="button"
            onClick={() => {
              if (
                (getRegistrationState(participant) === 'OFFBOARD' && !isRejoinWindowOpen(participant)) ||
                (getRegistrationState(participant) !== 'ACTIVE' && getRegistrationState(participant) !== 'OFFBOARD')
              ) {
                return;
              }
              setOffboardParticipantId('');
              setOffboardParticipantGender('');
              setShowOffboardDialog(true);
            }}
          >
            {getRegistrationState(participant) === 'OFFBOARD'
              ? isRejoinWindowOpen(participant)
                ? 'Rejoin participant'
                : 'Rejoin expired'
              : 'Offboard participant'}
          </a>
        </div>
      </div>
      <button onClick={() => navigate('/')} className="detail-back-button" title="Back to Dashboard">
        <svg viewBox="0 0 24 24">
          <path d="m12 19-7-7 7-7" />
          <path d="M19 12H5" />
        </svg>
      </button>

      <div className="detail-grid-container">
        <Grid
          data={gridData}
          className="participant-detail-grid"
          navigatable={false}
          sortable={false}
          filterable={false}
          pageable={false}
          resizable={false}
        >
          <Column field="field" title="Session number" width="180px" />
          {sessionColumns}
        </Grid>
      </div>

      <OffboardFormDialog
        isOpen={showOffboardDialog}
        participantId={offboardParticipantId}
        participantGender={offboardParticipantGender}
        expectedParticipantId={participant.id}
        expectedParticipantGender={getGenderDisplayName(participant.gender)}
        isRejoining={participant ? getRegistrationState(participant) === 'OFFBOARD' : false}
        onParticipantIdChange={setOffboardParticipantId}
        onParticipantGenderChange={setOffboardParticipantGender}
        onClose={() => setShowOffboardDialog(false)}
        onContinue={() => {
          setShowOffboardDialog(false);
          setShowConfirmDialog(true);
        }}
      />

      <OffboardConfirmDialog
        isOpen={showConfirmDialog}
        participantId={offboardParticipantId}
        isRejoining={participant ? getRegistrationState(participant) === 'OFFBOARD' : false}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={async () => {
          if (participant) {
            const isCurrentlyOffboard = getRegistrationState(participant) === 'OFFBOARD';
            const action = isCurrentlyOffboard ? 'rejoin' : 'offboard';
            setWasRejoinAction(isCurrentlyOffboard);

            try {
              const config = getConfig();
              const baseUrl = config.apiEndpoint;
              const actionUrl = `${baseUrl}/participants(${participant.participantUID})/${action}`;

              await axios.post(actionUrl);

              const participantUrl = `${baseUrl}/participants(${participantUID})`;
              const participantResponse = await axios.get(participantUrl);
              const updatedParticipant = processParticipantData(participantResponse.data.value);
              setParticipant(updatedParticipant);

              setShowConfirmDialog(false);
              setShowSuccessDialog(true);
            } catch (actionError) {
              setErrorMessage(`Failed to ${action} participant. Please try again.`);
              setShowConfirmDialog(false);
              setShowErrorDialog(true);
            }
          }
        }}
      />

      <OffboardSuccessDialog
        isOpen={showSuccessDialog}
        participantId={offboardParticipantId}
        isRejoining={wasRejoinAction}
        onClose={() => setShowSuccessDialog(false)}
      />

      <EnrollDeviceDialog
        isOpen={showEnrollDeviceDialog}
        onClose={() => setShowEnrollDeviceDialog(false)}
        onGenerateCode={async () => {
          setShowEnrollDeviceDialog(false);
          setShowGeneratingCodeDialog(true);

          try {
            if (participant) {
              const config = getConfig();
              const baseUrl = config.apiEndpoint;
              const response = await axios.post(`${baseUrl}/participants(${participant.participantUID})/devices`);

              if (!response.data?.id) {
                throw new Error('Failed to generate enrollment code. Please try again.');
              }

              setGeneratedCode(response.data.id);
              setShowGeneratingCodeDialog(false);
              setShowEnrollmentSuccessDialog(true);
            } else {
              throw new Error('No participant data available');
            }
          } catch (enrollError) {
            setShowGeneratingCodeDialog(false);
            setErrorMessage('Failed to generate enrollment code. Please try again.');
            setShowErrorDialog(true);
          }
        }}
      />

      <GeneratingCodeDialog isOpen={showGeneratingCodeDialog} />

      <EnrollmentSuccessDialog
        isOpen={showEnrollmentSuccessDialog}
        generatedCode={generatedCode}
        onClose={() => {
          setShowEnrollmentSuccessDialog(false);
          setGeneratedCode('');
        }}
      />

      <ErrorDialog isOpen={showErrorDialog} message={errorMessage} onClose={() => setShowErrorDialog(false)} />
    </div>
  );
};

export default DetailPage;
